import { Button, Paragraph, Edit, Delete, Label, Box } from 'component-library';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { type NewContainer, type AddContainers } from '@sr/types';
import {
  getContainerDisplayName,
  getOtherLoadCarrierTranslationKey,
  getPalletCollarAmount,
  isCustomContainerType,
} from '@sr/utils';
import { AddContainerModal } from '../AddContainerModal/AddContainerModal';
import { ContainerImage } from '../ContainerImage';
import {
  ContainerCardWrapper,
  ContainerDetailsInnerWrapper,
  ContainerDetailsWrapper,
  ButtonWrapper,
  MaterialOtherWrapper,
  ImageWrapper,
  MaterialWrapper,
  MaterialIdWrapper,
} from './styles';

export const ContainerCard = ({
  location,
  container,
  edit,
  removeContainer,
  onSave,
}: {
  location: string;
  container: AddContainers['containers'][number];
  edit?: boolean;
  removeContainer?: (id: string) => void;
  onSave?: (container: NewContainer) => void;
}) => {
  const { t } = useTranslation();
  const [showAddContainerModal, setShowAddContainerModal] = useState(false);

  if (!container.id) {
    return null;
  }
  const containerId = container.id;

  if (!container.containerType) {
    return null;
  }

  const showAmountAndDate = !isCustomContainerType(container.containerType);
  const palletCollarAmount = getPalletCollarAmount(container);
  let containerName = `${container.amount} x ${container.containerName}`;

  if (isCustomContainerType(container.containerType)) {
    const otherLoadCarrierTranslationKey = getOtherLoadCarrierTranslationKey(
      container.otherLoadCarrierType,
    );
    const displayContainerName = getContainerDisplayName(container, {
      sourceLabel: getCustomContainerSourceLabel(container.containerType, t),
      fallbackDisplayName: otherLoadCarrierTranslationKey
        ? t(otherLoadCarrierTranslationKey)
        : undefined,
      placeholderDisplayNames: [
        t(
          `rented-containers.add-containers.step1.add-container.modal.container.group.${container.containerType}`,
        ),
      ],
    });

    containerName = displayContainerName;
  }

  return (
    <ContainerCardWrapper>
      <ImageWrapper>
        <ContainerImage containerType={container.containerType} locationDwKey={location} />
      </ImageWrapper>
      <ContainerDetailsWrapper>
        <ContainerDetailsInnerWrapper>
          <Paragraph variant="large" bold>
            {showAmountAndDate ? `${container.amount} x ${container.containerName}` : containerName}
          </Paragraph>
          {palletCollarAmount > 0 && (
            <Paragraph variant="large">
              {`${palletCollarAmount} x ${t('rented-containers.add-containers.step1.add-container.container-card.pallet-collar')}`}
            </Paragraph>
          )}

          {container.materialName ? (
            <MaterialWrapper>
              <Paragraph variant="large">{container.materialName}</Paragraph>
              <MaterialIdWrapper>{`(${splitMaterialType(container.material)})`}</MaterialIdWrapper>
            </MaterialWrapper>
          ) : (
            <MaterialOtherWrapper>
              <Paragraph variant="large">{`“${container.materialOther}”`}</Paragraph>
            </MaterialOtherWrapper>
          )}
        </ContainerDetailsInnerWrapper>
        {showAmountAndDate ? (
          <ContainerDetailsInnerWrapper>
            <Box mt="xxsmall">
              <Label variant="info">
                {t('rented-containers.add-containers.step1.add-container.modal.date.label')}:&nbsp;
                {dayjs(container.date).format('YYYY-MM-DD')}
              </Label>
            </Box>
          </ContainerDetailsInnerWrapper>
        ) : null}
        {edit && (
          <ButtonWrapper>
            <Button
              iconPlacement="before"
              icon={<Edit />}
              variant="text-primary"
              onClick={() => {
                setShowAddContainerModal(true);
              }}
            >
              {t('rented-containers.add-containers.step1.add-container.container-card.edit')}
            </Button>

            <Button
              iconPlacement="before"
              variant="text-primary-red"
              icon={<Delete />}
              onClick={() => {
                if (!removeContainer) return;
                removeContainer(containerId);
              }}
            >
              {t('rented-containers.add-containers.step1.add-container.container-card.delete')}
            </Button>
          </ButtonWrapper>
        )}
      </ContainerDetailsWrapper>

      {location && onSave && (
        <AddContainerModal
          showModal={setShowAddContainerModal}
          isOpen={showAddContainerModal}
          editNewContainer={container}
          locationDwKey={location}
          onSave={onSave}
        />
      )}
    </ContainerCardWrapper>
  );
};

const splitMaterialType = (materialType: string | undefined) => {
  if (!materialType) return '';

  return materialType.split('_').slice(0).join(', ');
};

const getCustomContainerSourceLabel = (
  containerType: 'other_1' | 'other_2',
  translate: (key: string) => string,
) => {
  return translate(
    `rented-containers.add-containers.step1.add-container.modal.container.source-label.${containerType}`,
  );
};
